import styled from 'styled-components';

import { SANS_FONT_FAMILY } from '@stylesheets/font';

const PAD = '20px';
const GAP = '10px';

export const CardIcon = styled.span.attrs({ className: 'material-symbols-rounded' })<{ $size?: number }>`
  font-size: ${(p) => p.$size ?? 17}px;
  font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  flex-shrink: 0;
  line-height: 1;
`;

export const Dock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`;

export const EscapeHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font: 400 11.5px/1 ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surfaceContainer};
  overflow: hidden;
`;

export const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: ${PAD} ${PAD} 0;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 11px/1 ${SANS_FONT_FAMILY};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

export const Progress = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 5px;
`;

export const StepMeta = styled.span`
  text-align: center;
  font: 600 11px/1 ${SANS_FONT_FAMILY};
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const Bar = styled.div`
  height: 3px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.surfaceContainerHighest};
  overflow: hidden;
`;

export const BarFill = styled.span<{ $pct: number }>`
  display: block;
  height: 100%;
  border-radius: 2px;
  width: ${(p) => p.$pct}%;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 400ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

export const Question = styled.div`
  padding: 8px ${PAD} 0;
  font: 600 16px/22px ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurface};
`;

export const Options = styled.div`
  padding: 12px ${PAD};
  display: flex;
  flex-direction: column;
  gap: ${GAP};
`;

export const OptionRow = styled.div<{ $selected: boolean; $extra: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 11px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.outlineVariant)};
  background: ${({ theme, $selected }) =>
    $selected ? `color-mix(in oklch, ${theme.colors.primary} 12%, transparent)` : theme.colors.surfaceContainerLow};
  cursor: pointer;
  font: 400 13.5px/18px ${SANS_FONT_FAMILY};
  color: ${({ theme, $extra }) => ($extra ? theme.colors.onSurfaceVariant : theme.colors.onSurface)};
  transition: background 120ms, border-color 120ms;

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected ? `color-mix(in oklch, ${theme.colors.primary} 12%, transparent)` : theme.colors.surfaceContainerHigh};
  }
`;

export const OptionBadge = styled.span<{ $selected: boolean; $icon: boolean }>`
  width: 22px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font: 600 11px/1 ${SANS_FONT_FAMILY};
  letter-spacing: 0.3px;
  border: ${({ theme, $icon }) => ($icon ? '0' : `1px solid ${theme.colors.outline}`)};
  background: ${({ theme, $selected, $icon }) =>
    !$icon && $selected ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $selected, $icon }) =>
    !$icon && $selected ? theme.colors.onPrimary : theme.colors.onSurfaceVariant};
  transition: background 120ms, border-color 120ms, color 120ms;

  .material-symbols-rounded {
    font-size: 17px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const OptionLabel = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  min-width: 0;
`;

export const OptionInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  outline: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.onSurface};
  font: 400 13.5px/18px ${SANS_FONT_FAMILY};

  &::placeholder {
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
  }
`;

export const NoteInput = styled.input`
  margin: 0 ${PAD} 4px;
  padding: 11px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  background: ${({ theme }) => theme.colors.surfaceContainerLow};
  color: ${({ theme }) => theme.colors.onSurface};
  font: 400 13.5px/18px ${SANS_FONT_FAMILY};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
  }
`;

export const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 ${PAD} ${PAD};
`;

export const Spacer = styled.span`
  flex: 1;
`;

export const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font: 600 13px/1 ${SANS_FONT_FAMILY};
  letter-spacing: 0.1px;
  cursor: pointer;
  background: transparent;
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1), opacity 120ms;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceContainerHigh};
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;

export const FilledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font: 600 13px/1 ${SANS_FONT_FAMILY};
  letter-spacing: 0.1px;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1), opacity 120ms;

  &:hover {
    background: color-mix(in oklch, ${({ theme }) => theme.colors.primary} 92%, white);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/* ── Answers summary (the "My answers" user bubble after submit) ──────────── */

export const AnswersCard = styled.div`
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  border-top-right-radius: 4px;
  padding: 4px 4px 6px;
  max-width: 460px;
  display: flex;
  flex-direction: column;
`;

export const AnswersHead = styled.div`
  padding: 9px 12px 7px;
  font: 600 11px/1 ${SANS_FONT_FAMILY};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const AnswersRow = styled.div`
  padding: 8px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant};
`;

export const AnswersQuestion = styled.div`
  font: 400 11.5px/15px ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const AnswersAnswer = styled.div`
  font: 600 13px/18px ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurface};
  margin-top: 3px;
  text-wrap: pretty;
`;
